/** Retry transient read failures only. Writes must never be replayed implicitly. */
const TRANSIENT_STATUSES = new Set([429, 502, 503, 504]);
const MAX_RETRIES = 3;
const MAX_WAIT_MS = 30_000;

export function retryAfterMs(value: string | null, now = Date.now()): number | null {
  if (!value) return null;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const date = Date.parse(value);
  return Number.isFinite(date) ? Math.max(0, date - now) : null;
}

function wait(ms: number, signal?: AbortSignal | null): Promise<void> {
  return new Promise((resolve, reject) => {
    const abort = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
      reject(signal?.reason ?? new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, ms);
    signal?.addEventListener('abort', abort, { once: true });
    if (signal?.aborted) abort();
  });
}

export async function fetchWithReadRetry(url: string, options: RequestInit = {}): Promise<Response> {
  const method = (options.method ?? 'GET').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD') return fetch(url, options);
  let waited = 0;
  for (let attempt = 0; ; attempt++) {
    options.signal?.throwIfAborted();
    let response: Response | undefined;
    let networkError: unknown;
    try {
      response = await fetch(url, options);
      if (!TRANSIENT_STATUSES.has(response.status)) return response;
    } catch (error) {
      // Fetch uses TypeError for network failures. Never retry aborts or app errors.
      if (!(error instanceof TypeError) || options.signal?.aborted) throw error;
      networkError = error;
    }
    const backoff = 1000 * 2 ** attempt + Math.random() * 500;
    const delay = Math.max(backoff, retryAfterMs(response?.headers.get('Retry-After') ?? null) ?? 0);
    // A long server-requested wait must not be shortened to fit our budget.
    if (attempt >= MAX_RETRIES || waited + delay > MAX_WAIT_MS) {
      if (response) return response;
      throw networkError;
    }
    await response?.body?.cancel();
    await wait(delay, options.signal);
    waited += delay;
  }
}
